import React, { useState } from "react";
import { useMutation } from "@apollo/client";

import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentInd  from "@mui/icons-material/AssignmentInd";
import DeleteIcon from "@mui/icons-material/Delete";

import { Card, FormGroup } from "@cofano/cck";
import { DeletePurchaseCharter_CharterBlockDocument } from "__generated__/view/charters/graphql/charter.mutations.generated";
import { intl } from "LocaleWrapper";
import { useConfirm } from "components/hooks/useConfirm";
import { DataFormat, Language, usePermission } from "imports/Utils";
import { DetailsBlock, Height } from "../Blocks";
import { RelationLink } from "utils/LinkUtils";
import CharterSubtypeTag from "view/charters/components/tags/CharterSubtypeTag";
import { FormattedDateWindow } from "utils/DateUtils";
import { PurchaseCharterFromQuery } from "view/charters/PurchaseCharterDetails";
import PurchaseCharterCreateEditDialog from "view/charters/components/dialog/PurchaseCharterCreateEditDialog";

type PurchaseCharterDetailsBlockProps = {
	charter: PurchaseCharterFromQuery;
	color: string;
	refetch?: () => void;
	onDelete: () => void;
};

const PurchaseCharterDetailsBlock = ({ charter, color, refetch, onDelete }: PurchaseCharterDetailsBlockProps) => {

	const confirm = useConfirm();
	const { isReadOnly } = usePermission();

	const [ dialogOpen, openDialog ] = useState(false);

	const [ deleteMutation, { loading } ] = useMutation(DeletePurchaseCharter_CharterBlockDocument);

	const deleteCharter = () => {
		deleteMutation({
			variables: {
				id: charter.id,
				deleteChildren: false,
			},
		}).then(() => {
			onDelete();
		});
	};

	return (
		<React.Fragment>
			<DetailsBlock height={Height.Fill}>
				<Card
					color={color}
					icon={<AssignmentInd sx={{ color: color }} />}
					title={charter.charterNumber}
					actions={[ {
						icon: <EditIcon fontSize="small" />,
						hidden: isReadOnly,
						onClick: () => openDialog(true),
					}, {
						icon: <DeleteIcon fontSize="small" />,
						disabled: loading,
						hidden: isReadOnly,
						onClick: () => {
							confirm({
								type: "DELETE",
								content: <div>{intl.formatMessage({ id: "DELETE_PURCHASE_CHARTER_UNLINK_VOYAGES" })}</div>,
							}).then(() => {
								deleteCharter();
							});
						},
					} ]}
					disablePadding>
					<Box sx={{
						borderTop: 1,
						borderColor: "divider",
						height: "100%",
					}}>
						<div style={{ padding: 16 }}>
							<FormGroup label={intl.formatMessage({ id: "TYPE" })} labelPlacement="left-center" overflow="hidden">
								<CharterSubtypeTag type={charter.subtype} />
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "CONTRACTOR" })} labelPlacement="left-center" overflow="hidden">
								<RelationLink relation={charter.creditor} />
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "CUSTOMER" })} labelPlacement="left-center" overflow="hidden">
								<RelationLink relation={charter.debtor} />
							</FormGroup>

							{charter.validFrom != null && (<FormGroup label={Language("VALID_BETWEEN")} labelPlacement="left-center" overflow="hidden">
								<FormattedDateWindow from={charter.validFrom} to={charter.validUntil} format="ll" />
							</FormGroup>)}

							<FormGroup label={intl.formatMessage({ id: "CUSTOMER_REFERENCE" })} labelPlacement="left-center" overflow="hidden">
								{DataFormat.remarks(charter.customerReference, "-")}
							</FormGroup>
						</div>
					</Box>
				</Card>
			</DetailsBlock>

			<PurchaseCharterCreateEditDialog
				open={dialogOpen}
				onClose={() => openDialog(false)}
				charter={charter}
				refetch={refetch} />
		</React.Fragment>
	);
};


export default PurchaseCharterDetailsBlock;
